import { useCallback, useEffect, useRef } from 'react';

import { AI_BUILDER_CHAT_DRAWER_TOP_OFFSET } from '../components/AiBuilderShellTransition';

type MobileDrawerViewport = {
  bottom: number;
  top: number;
};

type QuestionComposerOverlayViewport = {
  bottom: number;
  maxHeight: number;
};

const DEFAULT_MOBILE_DRAWER_VIEWPORT: MobileDrawerViewport = {
  bottom: 0,
  top: AI_BUILDER_CHAT_DRAWER_TOP_OFFSET,
};

const finiteNonNegative = (value: number, fallback = 0) =>
  Number.isFinite(value) && value >= 0 ? value : fallback;

const getSafeInnerHeight = () => finiteNonNegative(window.innerHeight);

const getMobileDrawerViewport = (): MobileDrawerViewport => {
  const visualViewport = window.visualViewport;

  if (
    !visualViewport ||
    !Number.isFinite(visualViewport.height) ||
    visualViewport.height <= 0
  ) {
    return DEFAULT_MOBILE_DRAWER_VIEWPORT;
  }

  const offsetTop = finiteNonNegative(visualViewport.offsetTop);
  const innerHeight = getSafeInnerHeight();

  return {
    bottom: Math.max(innerHeight - offsetTop - visualViewport.height, 0),
    top: Math.max(AI_BUILDER_CHAT_DRAWER_TOP_OFFSET, offsetTop),
  };
};

const getQuestionComposerOverlayViewport = ({
  bottom,
  top,
}: MobileDrawerViewport): QuestionComposerOverlayViewport => ({
  // Overlay is absolute inside the already visualViewport-adjusted Drawer.
  // Reapplying the keyboard inset here would move it upward twice.
  bottom: 0,
  maxHeight: Math.max(getSafeInnerHeight() - top - bottom, 0),
});

export const useAiBuilderMobileDrawerViewport = (enabled: boolean) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const questionComposerOverlayRef = useRef<HTMLDivElement | null>(null);
  const updateViewport = useCallback(() => {
    const drawer = drawerRef.current;
    const viewport = enabled
      ? getMobileDrawerViewport()
      : DEFAULT_MOBILE_DRAWER_VIEWPORT;

    // 키보드 애니메이션 중 채팅 콘텐츠 전체가 다시 렌더되지 않게 한다.
    if (drawer) {
      drawer.style.bottom = `${viewport.bottom}px`;
      drawer.style.top = `${viewport.top}px`;
    }

    const questionComposerOverlay = questionComposerOverlayRef.current;
    if (questionComposerOverlay) {
      const overlayViewport = getQuestionComposerOverlayViewport(viewport);
      questionComposerOverlay.style.bottom = `${overlayViewport.bottom}px`;
      questionComposerOverlay.style.maxHeight = `${overlayViewport.maxHeight}px`;
      questionComposerOverlay.style.overflowY = 'auto';
    }
  }, [enabled]);
  const setDrawerRef = useCallback(
    (drawer: HTMLDivElement | null) => {
      drawerRef.current = drawer;
      updateViewport();
    },
    [updateViewport],
  );
  const setQuestionComposerOverlayRef = useCallback(
    (questionComposerOverlay: HTMLDivElement | null) => {
      questionComposerOverlayRef.current = questionComposerOverlay;
      updateViewport();
    },
    [updateViewport],
  );

  useEffect(() => {
    updateViewport();
    if (!enabled) return;

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('scroll', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, [enabled, updateViewport]);

  return {
    drawerRef,
    questionComposerOverlayRef,
    setDrawerRef,
    setQuestionComposerOverlayRef,
  };
};
